<template>
  <div class="source-content">
    <div class="filter-bar flex items-center">
      <div class="tb-form-group flex-grow margin-bottom-none">
        <i class="icon icon-magnifier"></i>

        <input
          type="search"
          class="tb-input tb-input--search"
          placeholder="Search saved products"
          v-model="keywordSearch"
          spellcheck="true"
          data-entity="producer-assets-searchShoppableProducts"
          data-test="producer-assets-shoppableSearch"
        />

        <button
          v-show="keywordSearch"
          class="search-clear-button"
          data-entity="producer-assets-clearShoppableProductSearch"
          data-test="producer-assets-clearShoppableSearch"
          @click="resetProductSearch"
        >
          <span class="fa fa-times"></span>
        </button>
      </div>
    </div>

    <!-- No boards after searching -->
    <div v-if="!isLoadingProducts && !productTotal && keywordSearch" class="text-center">
      <h3 class="margin-bottom-md">
        No products found
      </h3>

      <button
        class="tb-btn tb-btn-contained medium-em"
        data-entity="producer-assets-resetShoppableProductSearch"
        data-test="producer-assets-resetShoppableSearch"
        @click="resetProductSearch"
      >
        Reset Search
      </button>
    </div>

    <!-- Products -->
    <div
      ref="sourceList"
      class="source-list"
    >
      <draggable
        v-model="products"
        :sort="false"
        revert-on-spill
        :group="{ name: 'shoppable', pull: 'clone', put: false }"
        :move="onMove"
      >
        <div
          v-for="p in products"
          :key="p.id"
          class="source-item"
          :data-test="`producer-list-${p.id}`"
        >
          <div class="source-item-wrapper">
            <div class="source-image-wrapper margin-right-xs">
              <span class="product-store-icon" :class="storeIcon(p.source)"></span>
              <img
                class="source-image"
                :src="p.imageUrl"
                :alt="p.name"
                :data-test="`producer-list-${p.id}-image`"
              />
            </div>
            <div class="overflow-hidden margin-right-xs flex-grow">
              <div class="flex">
                <p class="margin-bottom-none" :data-test="`producer-list-${p.id}-price`">{{ p.price }} {{ p.currency }}</p>
                <p class="margin-left-sm margin-bottom-none" :data-test="`producer-list-${p.id}-sourceId`">({{ p.sourceId }})</p>
              </div>
              <p class="source-title title-text margin-bottom-none" :title="p.name" :data-test="`producer-list-${p.id}-name`">
                {{ p.name }}
              </p>
            </div>
            <a
              :href="p.sourceUrl"
              class="high-em"
              target="_blank"
              :data-test="`producer-list-${p.id}-sourceUrl`"
            ><span class="fas fa-external-link"></span></a>
          </div>
        </div>
      </draggable>

      <div ref="loadTrigger" class="load-trigger">
        <div v-if="isLoadingProducts" class="loading loading--inline" data-test="producer-assets-curatedContent-loading">
          <i class="icon icon-spinner"></i>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import { debounce } from 'lodash-es';
import { getStoreIcon } from '@/js/utils/shoppable';

import tabMixins from '../../../mixins/tab-mixins';

export default {
  name: 'ProductList',
  mixins: [tabMixins],
  props: {
    collectionId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      intersectionObserver: null,
    };
  },
  computed: {
    ...mapState('shoppable', [
      'products',
      'productTotal',
      'productSearch',
      'isLoadingProducts',
    ]),
    ...mapGetters('shoppable', [
      'hasMoreProducts',
    ]),

    keywordSearch: {
      get() {
        return this.productSearch;
      },
      set: debounce(function(search) {
        if (this.collectionId === -1) {
          this.searchProducts({ search });
        } else {
          this.searchProducts({ search, collectionId: this.collectionId });  
        }
      }, 500),
    },
  },
  watch: {
    isLoadingProducts(newVal) {
      if (!newVal && this.$refs.sourceList) {
        this.$nextTick(() => {
          if (this.$refs.sourceList.scrollHeight <= this.$refs.sourceList.offsetHeight) {
            // re-trigger observer
            this.intersectionObserver.unobserve(this.$refs.loadTrigger);
            this.intersectionObserver.observe(this.$refs.loadTrigger);
          }
        });
      }
    },
  },
  async created() {
    try {
      if (this.collectionId === -1) {
        await this.getProducts({ reset: true });
      } else {
        await this.getProducts({ collectionId: this.collectionId, reset: true });
      }
    } catch (error) {
      this.errorToast('Failed to load products.');
    }
  },
  mounted() {
    this.intersectionObserver = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        this.loadMore();      
      }
    }, {
      root: this.$refs.sourceList,
      threshold: 0,
    });

    this.intersectionObserver.observe(this.$refs.loadTrigger);
  },
  beforeDestroy() {
    this.setProducts([]);
    this.intersectionObserver.disconnect();
  },
  methods: {
    ...mapActions('shoppable', [
      'getProducts',
      'incrementProductPage',
      'searchProducts',
      'resetProductSearch',
      'setProducts',
    ]),
    onMove() {
      return -1; // always add to front so the dropzone can pop off the last item
    },

    async loadMore() {
      if (!this.isLoadingProducts && this.hasMoreProducts) {
        await this.incrementProductPage();
      }
    },

    storeIcon(store) {
      return getStoreIcon(store);
    },
  },
};
</script>

<style lang="scss">
.product-store-icon {
  position: absolute;
  top: 1px;
  left: 1px;
  font-size: 14px;
}
.fanatics-icon {
  color: #DB0C0C;
}
.amazon-icon {
  color: #05A0D1;
}
</style>
